import React, { useEffect, useState } from "react";
import { Mail, ArrowRight, ShieldCheck, KeyRound } from "lucide-react";
import { espaceApi, messageErreur, CLE_JETON } from "./api";

/* Une porte principale, sans rien à retenir : l'adresse e-mail reçoit un code à 6 chiffres
   (et un lien). Pas de compte à créer — le compte s'ouvre au premier code. Une famille de
   l'école retrouve ses enfants ; une adresse encore inconnue ouvre un espace vide, prêt
   pour une première inscription (école, 09.10.2026 : « quelqu'un qui n'est pas membre
   devrait pouvoir quand même se connecter s'il veut créer un compte et s'inscrire »).
   Google à côté, et le mot de passe pour qui en a choisi un. Le code sert aussi de « mot
   de passe oublié » : l'école n'a jamais à réinitialiser quoi que ce soit à la main
   (« le but c'est que les gens se connectent, pas qu'on les perde »). */
export const Connexion = ({ onGoogle, onConnectee }) => {
  // "code" (par défaut), "mot_de_passe" ou "creation" (choisir un mot de passe).
  const [mode, setMode] = useState("code");
  const [email, setEmail] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [parti, setParti] = useState("");
  const [erreur, setErreur] = useState("");
  const [code, setCode] = useState("");
  // Le bouton Google n'apparaît que si le serveur est configuré chez Google.
  const [google, setGoogle] = useState(false);
  useEffect(() => {
    espaceApi.get("/google/actif").then(({ data }) => setGoogle(!!data.actif)).catch(() => {});
  }, []);

  const changerMode = (m) => { setMode(m); setErreur(""); };

  const envoyer = async (e) => {
    e.preventDefault();
    setErreur("");
    setEnvoi(true);
    try {
      if (mode === "code") {
        const { data } = await espaceApi.post("/lien", { email, origine: window.location.origin });
        setParti(data.message);
      } else {
        const { data } = await espaceApi.post(mode === "creation" ? "/compte" : "/connexion", {
          email,
          mot_de_passe: motDePasse,
        });
        if (data.session) localStorage.setItem(CLE_JETON, data.session);
        // Adresse pas encore confirmée : on affiche l'écran « regarde ta boîte » plutôt
        // qu'un espace vide sans explication.
        if (data.verification_requise) setParti(data.message);
        else await onConnectee();
      }
    } catch (err) {
      setErreur(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  };

  /* Le code à 6 chiffres du courriel : il connecte l'écran où on l'a demandé — sur
     iPhone, le lien s'ouvrirait dans Safari et non dans l'application installée. */
  const validerCode = async (e) => {
    e.preventDefault();
    setErreur("");
    setEnvoi(true);
    try {
      const { data } = await espaceApi.post("/code", { email, code });
      if (data.session) localStorage.setItem(CLE_JETON, data.session);
      await onConnectee();
    } catch (err) {
      setErreur(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  };

  if (parti) {
    return (
      <div className="card-flat text-center" data-testid="espace-lien-envoye">
        <Mail size={32} className="mx-auto mb-4" style={{ color: "var(--primary)" }} />
        <div className="font-display text-xl mb-2">Regarde ta boîte e-mail</div>
        <p className="text-sm text-[var(--muted)]">{parti}</p>
        {email && (
          <form onSubmit={validerCode} className="mt-6 space-y-3 text-left" data-testid="espace-code">
            <label className="block text-sm font-semibold" htmlFor="espace-code-saisie">
              Tape ici le code à 6 chiffres reçu par e-mail
            </label>
            <input id="espace-code-saisie" inputMode="numeric" autoComplete="one-time-code"
              autoFocus maxLength={7} value={code} onChange={(e) => setCode(e.target.value)}
              className="input-field text-center text-2xl tracking-[0.4em]" placeholder="••• •••"
              data-testid="espace-code-saisie" />
            {erreur && <p className="text-sm" style={{ color: "#B91C1C" }}>{erreur}</p>}
            <button type="submit" disabled={envoi || code.replace(/\D/g, "").length !== 6}
              className="btn-primary w-full justify-center" data-testid="espace-code-valider">
              {envoi ? "Un instant…" : "Ouvrir mon espace"}
            </button>
            <p className="text-xs text-[var(--muted)] text-center">
              Ou clique simplement le bouton « Ouvrir mon espace » dans l'e-mail.
            </p>
          </form>
        )}
        <button onClick={() => { setParti(""); setCode(""); setErreur(""); }} className="btn-ghost mt-6"
          data-testid="espace-autre-email">
          Rien reçu ? Vérifier mon adresse
        </button>
      </div>
    );
  }

  const parMotDePasse = mode !== "code";

  return (
    <form onSubmit={envoyer} className="card-flat space-y-5" data-testid="espace-connexion">
      <div>
        <div className="eyebrow mb-2">Espace famille</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">
          {mode === "creation" ? "Choisis un mot de passe" : "Ton espace 1000 ARTS"}
        </h1>
        <p className="text-sm text-[var(--muted)] mt-3">
          Tes enfants, leurs cours, tes factures et tes attestations — au même endroit.
        </p>
      </div>

      {google && (
        <>
          <button type="button" onClick={onGoogle} className="btn-ghost w-full justify-center"
            data-testid="espace-google">
            Continuer avec Google
          </button>

          <div className="flex items-center gap-3 text-xs text-[var(--muted)]">
            <div className="h-px flex-1" style={{ background: "var(--border)" }} />
            ou
            <div className="h-px flex-1" style={{ background: "var(--border)" }} />
          </div>
        </>
      )}

      <div>
        <label className="field-label" htmlFor="espace-email">
          Ton e-mail (celui donné à l'inscription)
        </label>
        <input
          id="espace-email"
          data-testid="espace-email"
          type="email"
          required
          autoComplete="email"
          className="input-field"
          placeholder="prenom.nom@exemple.ch"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {!parMotDePasse && (
          <p className="text-xs text-[var(--muted)] mt-2" data-testid="espace-explication-code">
            Tu reçois un code à 6 chiffres par e-mail : rien à créer, aucun mot de passe à retenir.
            Pas encore inscrit·e ? Le même code t'ouvre un compte pour t'inscrire.
          </p>
        )}
      </div>

      {parMotDePasse && (
        <div>
          <label className="field-label" htmlFor="espace-mot-de-passe">
            {mode === "creation" ? "Choisis un mot de passe (8 caractères minimum)" : "Ton mot de passe"}
          </label>
          <input
            id="espace-mot-de-passe"
            data-testid="espace-mot-de-passe"
            type="password"
            required
            minLength={mode === "creation" ? 8 : undefined}
            autoComplete={mode === "creation" ? "new-password" : "current-password"}
            className="input-field"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
          />
        </div>
      )}

      {erreur && (
        <div className="text-sm rounded-2xl px-4 py-3" data-testid="espace-erreur"
          style={{ background: "#FEF2F2", color: "#B91C1C" }}>
          {erreur}
        </div>
      )}

      <button type="submit" disabled={envoi || !email} className="btn-primary w-full justify-center"
        data-testid="espace-valider">
        {envoi ? "Un instant…" : mode === "creation" ? "Enregistrer mon mot de passe"
          : mode === "mot_de_passe" ? "Me connecter" : "Recevoir mon code"}
        <ArrowRight size={16} />
      </button>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        {parMotDePasse ? (
          <button type="button" onClick={() => changerMode("code")}
            className="underline text-[var(--muted)] flex items-center gap-1"
            data-testid="espace-mot-de-passe-oublie">
            <KeyRound size={12} />
            {mode === "creation" ? "Plutôt recevoir un code" : "Mot de passe oublié ? Recevoir un code"}
          </button>
        ) : (
          <button type="button" onClick={() => changerMode("mot_de_passe")}
            className="underline text-[var(--muted)]" data-testid="espace-avec-mot-de-passe">
            J'ai un mot de passe
          </button>
        )}
        {mode === "mot_de_passe" && (
          <button type="button" onClick={() => changerMode("creation")}
            className="underline text-[var(--muted)]" data-testid="espace-bascule-creation">
            Choisir un mot de passe
          </button>
        )}
      </div>

      <p className="text-[11px] text-[var(--muted)] flex items-start gap-2">
        <ShieldCheck size={14} className="shrink-0 mt-0.5" />
        Tu ne vois que les informations de ta famille. C'est ton adresse e-mail qui ouvre
        l'accès à ta fiche — celle que l'école a enregistrée. Une autre adresse ouvre un
        espace vide, sans rien de ta famille.
      </p>
    </form>
  );
};
